import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { endpoints } from "@/lib/api";
import { useFan } from "@/lib/fanContext";
import { toast } from "sonner";
import { formatDate, eventPriceRange } from "@/lib/format";
import { CalendarDays, MapPin, Heart, Sparkles, ArrowUpRight, Building2 } from "lucide-react";

const RECS_STRIP_LIMIT = 5;

export default function FanOverview() {
    const { isFollowingVenue, followVenue } = useFan();
    const [data, setData] = useState(null);
    const [recs, setRecs] = useState([]);
    const [busyVenueId, setBusyVenueId] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        Promise.all([
            endpoints.fanOverview(),
            endpoints.fanRecommendedVenues(RECS_STRIP_LIMIT).catch(() => ({ recommendations: [] })),
        ]).then(([d, vr]) => {
            setData(d);
            setRecs((vr?.recommendations || []).slice(0, RECS_STRIP_LIMIT));
            setLoading(false);
        }).catch(() => setLoading(false));
    }, []);

    const doFollowVenue = async (v) => {
        setBusyVenueId(v.id);
        try {
            await followVenue(v.id, "fan_passport");
            toast.success(`Following ${v.name}`);
            setRecs((prev) => prev.filter((r) => r.venue.id !== v.id));
        } catch {
            toast.error("Follow failed");
        } finally { setBusyVenueId(null); }
    };

    if (loading) return <div className="p-6 md:p-10 font-mono text-sm text-[#666]" data-testid="fan-overview-loading">Loading passport…</div>;
    if (!data) return null;

    const s = data.summary;
    const isNewFan =
        (s.artists_followed || 0) === 0 &&
        (s.venues_followed || 0) === 0 &&
        (s.upcoming_events || 0) === 0 &&
        (s.attended_events || 0) === 0;

    return (
        <div data-testid="fan-overview-page" className="p-4 md:p-6 space-y-10">
            {isNewFan && (
                <section
                    className="border border-[#2a2a2a] bg-[#141414] p-5 md:p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4"
                    data-testid="fan-overview-new-fan-block"
                >
                    <div>
                        <div className="text-[10px] font-mono uppercase tracking-widest text-[#ff4400]">Start building your Passport</div>
                        <div className="mt-2 font-display font-black uppercase tracking-tighter text-xl md:text-2xl">
                            Follow artists and venues, discover events, and your activity will begin appearing here.
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <Link to="/artists" className="pm-btn" data-testid="new-fan-browse-artists">Browse Artists</Link>
                        <Link to="/events" className="pm-btn" data-testid="new-fan-browse-events">Browse Events</Link>
                        <Link to="/venues" className="pm-btn" data-testid="new-fan-browse-venues">Browse Venues</Link>
                    </div>
                </section>
            )}

            {/* Metric strip */}
            <section className="grid grid-cols-2 md:grid-cols-6 gap-px bg-[#2a2a2a] border border-[#2a2a2a]">
                <Metric label="Following" value={s.artists_followed} testId="metric-following" />
                <Metric label="Venues" value={s.venues_followed || 0} testId="metric-venues-followed" />
                <Metric label="Upcoming" value={s.upcoming_events} testId="metric-upcoming" />
                <Metric label="Attended" value={s.attended_events} testId="metric-attended" />
                <Metric label="Cities" value={s.cities_attended.length} sub={formatNamesWithMore(s.cities_attended)} testId="metric-cities" />
                <Metric label="Countries" value={s.countries_followed.length} sub={formatNamesWithMore(s.countries_followed)} testId="metric-countries" />
            </section>

            {/* Upcoming */}
            <section>
                <SectionHeader kicker="Coming up" title="Your upcoming events" right={<Link to="/fan/events" className="text-xs font-mono uppercase text-[#a3a3a3] hover:text-[#ff4400]" data-testid="overview-all-events-link">All events →</Link>} />
                {data.upcoming_events.length === 0 ? (
                    <EmptyState text="No upcoming events yet. Save one from an event page." action={<Link to="/events" className="pm-btn pm-btn-primary" data-testid="overview-browse-events">Browse events</Link>} testId="overview-no-upcoming" />
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#2a2a2a] border border-[#2a2a2a]">
                        {data.upcoming_events.map((e) => (
                            <Link key={e.id} to={`/events/${e.id}`} data-testid={`overview-upcoming-${e.id}`} className="group bg-[#0a0a0a] aspect-[4/3] relative overflow-hidden">
                                <img src={e.image_url} alt="" className="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500" />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
                                <div className="relative h-full w-full p-4 flex flex-col justify-between">
                                    <div className="bg-[#ff4400] px-2 py-1 text-[10px] font-mono uppercase w-fit">{formatDate(e.date)}</div>
                                    <div>
                                        <div className="text-[10px] font-mono uppercase text-[#a3a3a3]">{e.artist_name}</div>
                                        <div className="font-display font-black uppercase leading-tight mt-1">{e.name}</div>
                                        <div className="mt-1 text-[10px] font-mono uppercase text-[#a3a3a3] flex items-center gap-1">
                                            <MapPin className="w-3 h-3" /> {e.city}
                                        </div>
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                )}
            </section>

            {/* Upcoming from followed venues (dedup'd against artist + registered) */}
            {(data.upcoming_from_venues || []).length > 0 && (
                <section>
                    <SectionHeader kicker="From venues you follow" title="Also coming up at your venues" right={<Link to="/fan/venues" className="text-xs font-mono uppercase text-[#a3a3a3] hover:text-[#ff4400]" data-testid="overview-all-venues-link">All venues →</Link>} />
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#2a2a2a] border border-[#2a2a2a]">
                        {data.upcoming_from_venues.map((e) => (
                            <Link key={e.id} to={`/events/${e.id}`} data-testid={`overview-venue-upcoming-${e.id}`} className="group bg-[#0a0a0a] aspect-[4/3] relative overflow-hidden">
                                <img src={e.image_url} alt="" className="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500" />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
                                <div className="relative h-full w-full p-4 flex flex-col justify-between">
                                    <div className="bg-[#ff9500] text-black px-2 py-1 text-[10px] font-mono uppercase w-fit">{e.venue_name}</div>
                                    <div>
                                        <div className="text-[10px] font-mono uppercase text-[#a3a3a3]">{formatDate(e.date)} · {e.artist_name}</div>
                                        <div className="font-display font-black uppercase leading-tight mt-1">{e.name}</div>
                                        <div className="mt-1 text-[10px] font-mono uppercase text-[#a3a3a3] flex items-center gap-1">
                                            <MapPin className="w-3 h-3" /> {e.city}
                                        </div>
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                </section>
            )}

            {/* Compact Recommended Venues strip */}
            {recs.length > 0 && (
                <section data-testid="overview-rec-venues-section">
                    <SectionHeader
                        kicker={<span className="flex items-center gap-1"><Building2 className="w-3 h-3" /> For you</span>}
                        title="Venues you might like"
                        right={<Link to="/venues" className="text-xs font-mono uppercase text-[#a3a3a3] hover:text-[#ff4400]" data-testid="overview-rec-venues-all-link">More →</Link>}
                    />
                    <div className="flex md:grid md:grid-cols-3 lg:grid-cols-5 gap-px bg-[#2a2a2a] border border-[#2a2a2a] overflow-x-auto md:overflow-visible snap-x snap-mandatory md:snap-none">
                        {recs.map((r) => {
                            const v = r.venue;
                            const following = isFollowingVenue(v.id);
                            return (
                                <div key={v.id} className="bg-[#0a0a0a] p-3 flex flex-col shrink-0 w-[68vw] md:w-auto snap-start" data-testid={`overview-rec-venue-${v.id}`}>
                                    <Link to={`/venues/${v.id}`} className="block relative aspect-[16/10] overflow-hidden group">
                                        {v.cover_url || v.image_url ? (
                                            <img src={v.cover_url || v.image_url} alt="" className="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500" />
                                        ) : <div className="absolute inset-0 bg-[#141414]" />}
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                                        <div className="absolute bottom-2 left-2 right-2">
                                            <div className="text-[10px] font-mono uppercase text-[#ff4400]">Venue</div>
                                            <div className="font-display font-black uppercase text-base leading-tight text-[#f4f4f0] truncate">{v.name}</div>
                                        </div>
                                    </Link>
                                    <div className="mt-2 text-[10px] font-mono uppercase text-[#a3a3a3] flex items-center gap-1"><MapPin className="w-3 h-3" /> {v.city}, {v.country}</div>
                                    <div className="mt-1 text-xs text-[#f4f4f0] leading-snug border-l-2 border-[#ff4400] pl-2 line-clamp-2" data-testid={`overview-rec-venue-reason-${v.id}`}>
                                        {r.reason}
                                    </div>
                                    {r.upcoming_event && (
                                        <Link to={`/events/${r.upcoming_event.id}`} className="mt-2 block text-[10px] font-mono uppercase text-[#a3a3a3] hover:text-[#ff4400] truncate" data-testid={`overview-rec-venue-next-${v.id}`}>
                                            <CalendarDays className="w-3 h-3 inline mr-1" />
                                            {formatDate(r.upcoming_event.date)} · {r.upcoming_event.name}
                                        </Link>
                                    )}
                                    <div className="mt-2 flex gap-2">
                                        <button
                                            onClick={() => !following && doFollowVenue(v)}
                                            disabled={following || busyVenueId === v.id}
                                            className={`pm-btn text-[10px] py-1 flex-1 ${following ? "" : "pm-btn-primary"}`}
                                            data-testid={`overview-rec-venue-follow-${v.id}`}
                                        >
                                            <Heart className="w-3 h-3 mr-1" fill={following ? "currentColor" : "none"} />
                                            {following ? "Following" : "Follow"}
                                        </button>
                                        <Link to={`/venues/${v.id}`} className="pm-btn text-[10px] py-1" data-testid={`overview-rec-venue-view-${v.id}`}>
                                            View <ArrowUpRight className="w-3 h-3 ml-1" />
                                        </Link>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </section>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Recently followed */}
                <section>
                    <SectionHeader kicker="You're following" title="Recently followed" right={<Link to="/fan/following" className="text-xs font-mono uppercase text-[#a3a3a3] hover:text-[#ff4400]" data-testid="overview-all-following-link">All →</Link>} />
                    {data.recent_followed.length === 0 ? (
                        <EmptyState text="Follow your first artist from any artist page." action={<Link to="/artists" className="pm-btn" data-testid="overview-browse-artists">Browse artists</Link>} testId="overview-no-following" />
                    ) : (
                        <div className="border border-[#2a2a2a]">
                            {data.recent_followed.map((a) => (
                                <Link key={a.id} to={`/artists/${a.id}`} data-testid={`overview-recent-follow-${a.id}`} className="flex items-center gap-3 p-3 border-b border-[#2a2a2a] last:border-b-0 group hover:bg-[#141414]">
                                    <img src={a.image_url} alt="" className="w-12 h-12 object-cover grayscale group-hover:grayscale-0" />
                                    <div className="flex-1 min-w-0">
                                        <div className="font-display font-bold uppercase truncate">{a.name}</div>
                                        <div className="text-[10px] font-mono uppercase text-[#a3a3a3]">{a.genre} · {a.city}</div>
                                    </div>
                                    <Heart className="w-4 h-4 text-[#ff4400]" fill="currentColor" />
                                </Link>
                            ))}
                        </div>
                    )}
                </section>

                {/* Recently attended */}
                <section>
                    <SectionHeader kicker="Been there" title="Recently attended" />
                    {data.recent_attended.length === 0 ? (
                        <EmptyState
                            text="Events you attend will appear here after check-in."
                            action={<Link to="/events" className="pm-btn" data-testid="overview-attended-browse-events">Browse events</Link>}
                            testId="overview-no-attended"
                        />
                    ) : (
                        <div className="border border-[#2a2a2a]">
                            {data.recent_attended.map((e) => (
                                <Link key={e.id} to={`/events/${e.id}`} data-testid={`overview-recent-attended-${e.id}`} className="flex items-center gap-3 p-3 border-b border-[#2a2a2a] last:border-b-0 group hover:bg-[#141414]">
                                    <img src={e.image_url} alt="" className="w-12 h-12 object-cover grayscale group-hover:grayscale-0" />
                                    <div className="flex-1 min-w-0">
                                        <div className="font-display font-bold uppercase truncate">{e.name}</div>
                                        <div className="text-[10px] font-mono uppercase text-[#a3a3a3]">{formatDate(e.date)} · {e.city}</div>
                                    </div>
                                    <div className="text-[10px] font-mono uppercase text-[#00e676]">Attended</div>
                                </Link>
                            ))}
                        </div>
                    )}
                </section>
            </div>

            {/* Venues you follow — always render an anchor with either the section
                or an empty-state so a new fan sees the "Follow venues" affordance and
                never gets an unexplained blank column. */}
            <section data-testid="overview-followed-venues-section">
                <SectionHeader
                    kicker={<span className="flex items-center gap-1"><Building2 className="w-3 h-3" /> Venues</span>}
                    title="Venues you follow"
                    right={
                        (s.venues_followed || 0) > 0 ? (
                            <Link to="/fan/venues" className="text-xs font-mono uppercase text-[#a3a3a3] hover:text-[#ff4400]" data-testid="overview-followed-venues-all-link">All venues →</Link>
                        ) : null
                    }
                />
                {(s.venues_followed || 0) === 0 ? (
                    <EmptyState
                        text="Follow venues to keep up with shows and events happening there."
                        action={<Link to="/venues" className="pm-btn pm-btn-primary" data-testid="overview-browse-venues">Browse Venues</Link>}
                        testId="overview-no-followed-venues"
                    />
                ) : (
                    <div className="text-xs font-mono uppercase text-[#a3a3a3]" data-testid="overview-followed-venues-hint">
                        {(s.venues_followed || 0)} venue{s.venues_followed === 1 ? "" : "s"} followed —{" "}
                        <Link to="/fan/venues" className="text-[#ff4400] hover:text-[#f4f4f0]">manage venues</Link>
                    </div>
                )}
            </section>

            {/* Suggested */}
            <section>
                <SectionHeader kicker={<span className="flex items-center gap-1"><Sparkles className="w-3 h-3" /> Suggested</span>} title="Artists you might like" right={<Link to="/" className="text-xs font-mono uppercase text-[#a3a3a3] hover:text-[#ff4400]" data-testid="overview-all-discover-link">Full discover →</Link>} />
                {data.suggested_artists.length === 0 ? (
                    <EmptyState text="Follow a few artists to unlock suggestions." testId="overview-no-suggested" />
                ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-[#2a2a2a] border border-[#2a2a2a]">
                        {data.suggested_artists.map((a) => (
                            <Link key={a.id} to={`/artists/${a.id}`} data-testid={`overview-suggested-${a.id}`} className="group bg-[#0a0a0a] p-3">
                                <div className="relative aspect-square overflow-hidden">
                                    <img src={a.image_url} alt="" className="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all" />
                                </div>
                                <div className="mt-2 font-display font-bold uppercase text-sm truncate">{a.name}</div>
                                <div className="text-[10px] font-mono uppercase text-[#a3a3a3]">{a.genre}</div>
                            </Link>
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
}

function Metric({ label, value, sub, testId }) {
    return (
        <div className="bg-[#0a0a0a] p-4 md:p-5" data-testid={testId}>
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#666]">{label}</div>
            <div className="mt-2 font-display font-black uppercase tracking-tighter text-3xl md:text-4xl">{value}</div>
            {sub && <div className="text-[10px] font-mono uppercase text-[#a3a3a3] truncate mt-1">{sub}</div>}
        </div>
    );
}

function SectionHeader({ kicker, title, right }) {
    return (
        <div className="flex items-end justify-between gap-4 mb-4">
            <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#666]">{kicker}</div>
                <h2 className="mt-1 font-display font-black uppercase tracking-tighter text-2xl md:text-3xl">{title}</h2>
            </div>
            {right}
        </div>
    );
}

function EmptyState({ text, action, testId }) {
    return (
        <div className="border border-dashed border-[#2a2a2a] p-8 text-center flex flex-col items-center gap-3" data-testid={testId}>
            <div className="text-sm text-[#a3a3a3]">{text}</div>
            {action}
        </div>
    );
}

// Formats a list of names for the Cities / Countries stat cards.
//   0 items  → ""
//   1 item   → "Argentina"
//   2 items  → "Argentina, Colombia"
//   3+ items → "Argentina, Colombia +N"
function formatNamesWithMore(names) {
    if (!Array.isArray(names) || names.length === 0) return "";
    if (names.length === 1) return names[0];
    if (names.length === 2) return `${names[0]}, ${names[1]}`;
    return `${names[0]}, ${names[1]} +${names.length - 2}`;
}
